import {
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import {
    RecordContextProvider,
    ReferenceManyField,
    ShowBase,
    TabbedShowLayout,
    useGetMany,
    useShowContext,
} from 'react-admin';
import { AnimationLightCard } from '@app/animation/show/AnimationLightCard';
import { AddressLink } from '@app/misc/AddressLink';
import { ExpandableRichTextField } from '@app/misc/ExpandableRichTextField';
import { MissionSummary } from '@wwf/sdk';
import { MissionShowAside } from './MissionShowAside';
import { LabelValueRow } from '@app/misc/LabelValueRow';
import { LabelValueColumn } from '@app/misc/LabelValueColumn';
import { MissionDateChip } from '../chips/MissionDateChip';
import { MissionTypeChip } from '../chips/MissionTypeChip';
import { StudyFormChip } from '../chips/StudyFormChip';
import { NotesLayout } from '@app/notes';
import { MissionSessionsTab } from './MissionSessionsTab';
import { MediaGridLayout } from '@app/media/MediaGridLayout';
import { StudyFormStatisticsTab } from './StudyFormStatisticsTab';
import { MissionReportTab } from './MissionReportTab';
import { MissionSessionIcon } from '../MissionSessionIcon';
import { ReportIcon } from '../ReportIcon';
import { MediaIcon } from '@app/media/MediaIcon';
import { ActivityIcon } from '@app/activity/ActivityIcon';
import StatsIcon from '@mui/icons-material/BarChart';

import { MissionVisibilityChip } from '../chips/MissionVisibilityChip';
export const MissionShow = () => (
    <ShowBase resource="mission">
        <MissionShowContent />
    </ShowBase>
);

const MissionShowContent = () => {
    const { record, isPending } = useShowContext<MissionSummary>();

    if (isPending || !record) return null;

    return (
        <Grid container spacing={2} mt={1} rowGap={4}>
            <Grid item xs={12} md={9} sx={{ minWidth: 0 }}>
                <Stack
                    flex="1"
                    flexDirection="column"
                    justifyContent="flex-start"
                    gap={2}
                    sx={{ minWidth: 0, width: '100%' }}
                >
                    <InfoCard record={record} />
                    <TabCard record={record} />
                </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
                <MissionShowAside />
            </Grid>
        </Grid>
    );
};

const MissionAnimationsField = ({ mission }: { mission: MissionSummary }) => {
    const animationIds = mission.animations ?? [];

    const { data: animations } = useGetMany(
        'animation',
        { ids: animationIds, meta: { antennaId: mission.antenna?.id } },
        { enabled: animationIds.length > 0 }
    );

    if (!animationIds.length) {
        return <LabelValueRow label="Animations" value="None" />;
    }
    if (!animations?.length) return null;

    return (
        <LabelValueRow
            label="Animations"
            value={
                <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
                    {animations.map(animation => (
                        <RecordContextProvider
                            key={animation.id}
                            value={animation}
                        >
                            <AnimationLightCard />
                        </RecordContextProvider>
                    ))}
                </Stack>
            }
        />
    );
};

const ArchivedTitle = () => (
    <Box
        sx={{
            background: theme => theme.palette.warning.main,
            px: 2,
            py: 1,
            mb: 2,
            borderRadius: 1,
        }}
    >
        <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
                color: theme => theme.palette.warning.contrastText,
            }}
        >
            Archived Mission
        </Typography>
    </Box>
);

const InfoCard = ({ record }: { record: MissionSummary }) => {
    return (
        <Card>
            <CardContent>
                <Box display="flex" mb={1} alignItems="center">
                    <Typography variant="h5" flex="1">
                        {record.name}
                    </Typography>
                    <Box display="flex" mr={1}>
                        {record.startDate &&
                        new Date(record.startDate) < new Date() ? (
                            <Chip label="Past" color="error" size="small" />
                        ) : null}
                    </Box>
                </Box>

                {record.status === 'ARCHIVED' && <ArchivedTitle />}

                <Stack direction="row" mb={2} mt={2} gap={2}>
                    <Box display="flex" flexDirection="column" width="70%">
                        <LabelValueRow
                            label="Expected date"
                            value={
                                <>
                                    <MissionDateChip date={record.startDate} />
                                    {' to '}
                                    <MissionDateChip date={record.endDate} />
                                </>
                            }
                        />

                        <LabelValueRow
                            label="Address"
                            value={
                                <AddressLink
                                    maxWidth={250}
                                    address={record.address}
                                />
                            }
                        />

                        <LabelValueRow
                            label="Partner"
                            value={record.partnerInfo ?? 'No partner'}
                        />

                        <MissionAnimationsField mission={record} />

                        <LabelValueRow
                            label="Study form"
                            value={
                                record.studyFormId ? (
                                    <StudyFormChip id={record.studyFormId} />
                                ) : (
                                    'No study form'
                                )
                            }
                        />
                    </Box>
                    <Box display="flex" flexDirection="column">
                        <LabelValueRow
                            label="Type"
                            value={<MissionTypeChip type={record.type} />}
                        />
                        <LabelValueRow
                            label="Who can see"
                            value={
                                record.whoCanSee && (
                                    <MissionVisibilityChip
                                        visibility={record.whoCanSee}
                                    />
                                )
                            }
                        />
                        <LabelValueRow
                            label="Who can participate"
                            value={
                                record.whoCanParticipate && (
                                    <MissionVisibilityChip
                                        visibility={record.whoCanParticipate}
                                    />
                                )
                            }
                        />
                        <LabelValueRow
                            label="With Sales"
                            value={record.withSales ? 'Yes' : 'No'}
                        />

                        <LabelValueRow
                            label="Participants limit"
                            value={record.participantLimit ?? 'No limit'}
                        />

                        <LabelValueRow
                            label="Visio conference"
                            value={
                                record.visioConferenceLink ? (
                                    <Typography
                                        component="a"
                                        href={record.visioConferenceLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        sx={{
                                            color: 'primary.main',
                                            textDecoration: 'underline',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: 250,
                                            display: 'block',
                                        }}
                                    >
                                        Join conference
                                    </Typography>
                                ) : (
                                    '-'
                                )
                            }
                        />
                    </Box>
                </Stack>

                <LabelValueColumn
                    label="Description"
                    value={
                        <ExpandableRichTextField
                            source="description"
                            maxHeight={100}
                        />
                    }
                />
            </CardContent>
        </Card>
    );
};

const TabCard = ({ record }: { record: MissionSummary }) => {
    return (
        <Card sx={{ minWidth: 0, maxWidth: '100%' }}>
            <CardContent sx={{ minWidth: 0 }}>
                <TabbedShowLayout
                    sx={{
                        minWidth: 0,
                        maxWidth: '100%',
                        width: '100%',
                        '& .RaTabbedShowLayout-content': { p: 0, minWidth: 0 },
                    }}
                >
                    <TabbedShowLayout.Tab
                        label={
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                            >
                                {!(record.sessions?.length ?? 0)
                                    ? 'No Sessions'
                                    : record.sessions!.length === 1
                                      ? '1 Session'
                                      : `${record.sessions!.length} Sessions`}
                                <MissionSessionIcon />
                            </Stack>
                        }
                    >
                        <MissionSessionsTab record={record} />
                    </TabbedShowLayout.Tab>
                    <TabbedShowLayout.Tab
                        label={
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                            >
                                {!record.nbNotes
                                    ? 'No Notes'
                                    : record.nbNotes === 1
                                      ? '1 Note'
                                      : `${record.nbNotes} Notes`}
                                <ActivityIcon />
                            </Stack>
                        }
                        path="notes"
                    >
                        <ReferenceManyField
                            target="missionId"
                            reference="missionNote"
                            sort={{ field: 'createdAt', order: 'DESC' }}
                        >
                            <NotesLayout reference="mission" />
                        </ReferenceManyField>
                    </TabbedShowLayout.Tab>
                    <TabbedShowLayout.Tab
                        label={
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                            >
                                {!record.report ? 'No Report' : 'Report'}
                                <ReportIcon />
                            </Stack>
                        }
                        path="report"
                    >
                        <MissionReportTab record={record} />
                    </TabbedShowLayout.Tab>
                    {record.studyFormId && (
                        <TabbedShowLayout.Tab
                            label={
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={0.5}
                                >
                                    {`Stats : ${record.totalTouchedPersonCount} persons`}
                                    <StatsIcon />
                                </Stack>
                            }
                            path="form_statistics"
                        >
                            <StudyFormStatisticsTab mission={record} />
                        </TabbedShowLayout.Tab>
                    )}
                    <TabbedShowLayout.Tab
                        label={
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                            >
                                {!record.nbMedias
                                    ? 'No Medias'
                                    : record.nbMedias === 1
                                      ? '1 Media'
                                      : `${record.nbMedias} Medias`}
                                <MediaIcon />
                            </Stack>
                        }
                        path="medias"
                    >
                        <ReferenceManyField
                            target="missionId"
                            reference="missionMedia"
                            sort={{ field: 'createdAt', order: 'DESC' }}
                        >
                            <MediaGridLayout missionId={record.id} />
                        </ReferenceManyField>
                    </TabbedShowLayout.Tab>
                </TabbedShowLayout>
            </CardContent>
        </Card>
    );
};
